import React, { useState, useEffect, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  // Badge removed,
  Tooltip,
  useMediaQuery,
  Button,
  Link,
  Breadcrumbs,
} from '@mui/material';
import useModalBehavior from '../hooks/useModalBehavior';
import * as modalUtils from '../utils/modalUtils';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  VpnKey as ApiKeyIcon,
  // SettingsIcon removed,
  // AssessmentIcon removed,
  Code as CodeIcon,
  // AccountCircleIcon removed,
  MenuBook as MenuBookIcon,
  // NotificationsIcon removed
  ExpandLess as ExpandLessIcon,
  NavigateNext as NavigateNextIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  Rocket as RocketIcon,
  Support as SupportIcon,
  GitHub as GitHubIcon,
  Science as ScienceIcon,
  Share as ShareIcon,
  PersonAdd as PersonAddIcon,
  Apps as AppsIcon,
  AppShortcut as AppShortcutIcon,
  DesignServices as DesignServicesIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  Terminal as TerminalIcon,
  Group as GroupIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  GridView as GridViewIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Security as SecurityIcon,
  LockPerson as LockPersonIcon,
  Article as ArticleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Permission, Role } from '../types/rbac';
import { hasAnyPermission } from '../utils/rbac';
import ThemeToggle from '../components/ui/ThemeToggle';
import { AnnouncementBanner, ClickAwayListener } from '../components/ui';
import ReferralModal from '../components/ReferralModal';
import SettingsModal from '../components/SettingsModal';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { Language as LanguageIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { APP_CONFIG } from '../config';

// Responsive drawer width
const drawerWidth = 220; // Reduced for more compact layout
const mobileDrawerWidth = '100%';

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  requiredPermissions?: Permission[];
  guestAccessible?: boolean;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}

interface MenuGroup {
  title?: string;
  items: MenuItem[];
}

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0, // Remove padding from outer container
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  overflowX: 'hidden', // Prevent horizontal scrolling
  width: '100%', // Take full width when drawer is closed
  minWidth: '100%', // Ensure minimum width is also 100%
  maxWidth: '100vw', // Limit width to viewport
  boxSizing: 'border-box', // Include padding in width calculation
  display: 'flex', // Use flexbox for centering
  flexDirection: 'column', // Stack children vertically
  ...(open && {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `${drawerWidth}px`,
    width: `calc(100% - ${drawerWidth}px)`, // Adjust width when drawer is open
    minWidth: `calc(100% - ${drawerWidth}px)`, // Adjust min-width when drawer is open
  }),
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%', // Always full width on mobile
    minWidth: '100%', // Always full min-width on mobile
  },
}));

const AppBarStyled = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<{
  open?: boolean;
}>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.06)',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  '& .MuiToolbar-root': {
    minHeight: '56px',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(1.5),
      height: '56px',
    },
  },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  minHeight: '56px',
  justifyContent: 'space-between',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
}));

const Logo = styled('img')({
  height: 24,
  marginRight: 6,
});

const MenuSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

const MenuSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  padding: theme.spacing(0, 2),
  marginBottom: theme.spacing(1),
}));

interface DashboardLayoutProps {
  children: React.ReactNode;
  isGuestMode?: boolean;
  onOpenReferralModal?: () => void;
  announcementProps?: {
    message: string;
    ctaText?: string;
    ctaLink?: string;
    bannerColor?: string;
    textColor?: string;
    bannerId?: string;
    enabled?: boolean;
  };
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  isGuestMode = false,
  onOpenReferralModal,
  announcementProps
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const { breadcrumbs } = useBreadcrumbs();
  const { getMenuProps, getDialogProps, getDrawerProps } = useModalBehavior();
  const { toggleColorMode } = useThemeContext();
  const { t } = useTranslation();

  // Determine which logo to use based on theme mode
  // @important: Keep using SVG logos for the dashboard UI
  const logoSrc = theme.palette.mode === 'dark' ? '/assets/logo_neg.svg' : '/assets/logo.svg';

  const [open, setOpen] = useState(!isMobile);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  // Notification bell removed
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);

  // Check if user has any of the required permissions
  const hasRequiredPermissions = (requiredPermissions: Permission[]): boolean => {
    if (isGuestMode) return false;
    if (!user) return false;
    return hasAnyPermission(user, requiredPermissions);
  };

  // Check if user has admin or super admin role
  const isAdmin = user?.role === Role.ADMIN || user?.role === Role.SUPER_ADMIN;
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;
  const isAppManager = user?.role === Role.APP_MANAGER;
  const isUserOrAppManager = user?.role === Role.USER || user?.role === Role.APP_MANAGER;

  // Close drawer on mobile by default
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [isMobile]);

  // Track previous pathname to detect actual navigation changes
  const prevPathnameRef = useRef(location.pathname);

  // Close drawer when location changes (route navigation) on mobile
  useEffect(() => {
    // Only close if the pathname actually changed and drawer is open on mobile
    if (isMobile && open && prevPathnameRef.current !== location.pathname) {
      // Use a small delay to ensure the drawer closes after navigation
      const timer = setTimeout(() => {
        setOpen(false);
      }, 50);

      // Update the ref with current pathname
      prevPathnameRef.current = location.pathname;

      return () => clearTimeout(timer);
    } else {
      // Just update the ref without closing the drawer
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname, isMobile, open]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  // Import the utility function instead of implementing it here
  // This is just a wrapper around the utility function
  const cleanupModalElements = () => {
    // Call the utility function with immediate=false to wait for transitions
    modalUtils.cleanupModalElements(false);

    // Additional fix for the problematic MuiDrawer-docked class
    setTimeout(() => {
      const dockedDrawer = document.querySelector('.MuiDrawer-docked');
      if (dockedDrawer && !open) {
        (dockedDrawer as HTMLElement).style.position = 'absolute';
        (dockedDrawer as HTMLElement).style.width = '0';
        (dockedDrawer as HTMLElement).style.overflow = 'hidden';
      }
    }, 300);
  };

  const handleDrawerClose = () => {
    // Close the drawer
    setOpen(false);

    // Force cleanup of any lingering backdrop or overlay
    cleanupModalElements();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
    cleanupModalElements();
  };

  // No longer needed as we're using useModalBehavior

  // Notification handlers removed

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  const toggleHelpMenu = () => {
    setHelpMenuOpen(!helpMenuOpen);
  };

  // @important: Menu items with required permissions grouped by category
  const menuGroups: MenuGroup[] = isUserOrAppManager ? [
    // Special simplified menu for USER and APP_MANAGER roles
    {
      title: t('common.appManagement'),
      items: [
        { text: t('common.myApps'), icon: <GridViewIcon />, path: '/my-apps', requiredPermissions: [Permission.USE_APP_STORE] },
        // { text: t('common.appLibrary'), icon: <DesignServicesIcon />, path: '/deploy-app/rag-chatbot', requiredPermissions: [Permission.DEPLOY_APPS] }
      ]
    },
    // Hide development tools for APP_MANAGER role
    ...(!isAppManager ? [{
      title: t('common.development'),
      items: [
        { text: t('navigation.apiKeys'), icon: <ApiKeyIcon />, path: '/api-keys', requiredPermissions: [Permission.USE_API] },
        { text: t('navigation.playground'), icon: <TerminalIcon />, path: '/playground', requiredPermissions: [Permission.USE_API] },
        { text: t('navigation.documentation'), icon: <ArticleIcon />, path: '/docs', requiredPermissions: [Permission.VIEW_DOCS] }
      ]
    }] : [])
  ] : [
    // Regular menu for other roles
    {
      title: t('common.main'),
      items: [
        { text: t('common.dashboard'), icon: <DashboardIcon />, path: '/dashboard' }
      ]
    },
    {
      title: t('common.apps'),
      items: [
        { text: t('common.myApps'), icon: <GridViewIcon />, path: '/my-apps', requiredPermissions: [Permission.USE_APP_STORE] },
        // { text: t('common.appLibrary'), icon: <DesignServicesIcon />, path: '/deploy-app/rag-chatbot', requiredPermissions: [Permission.DEPLOY_APPS], adminOnly: true },
        ...(user?.role === Role.SUPER_ADMIN ? [
          {
            text: t('common.appTemplates'),
            icon: <AppShortcutIcon />,
            path: '/app-templates-management',
            requiredPermissions: [Permission.MANAGE_APP_STORE],
            superAdminOnly: true
          }
        ] : [])
      ]
    },
    {
      title: t('common.development'),
      items: [
        { text: t('navigation.apiKeys'), icon: <ApiKeyIcon />, path: '/api-keys' },
        { text: t('navigation.playground'), icon: <TerminalIcon />, path: '/playground' },
        { text: t('navigation.documentation'), icon: <ArticleIcon />, path: '/docs' }
      ]
    },
    {
      title: t('common.administration'),
      items: [
        ...(user?.role === Role.SUPER_ADMIN ? [
          {
            text: t('navigation.users'),
            icon: <PeopleIcon />,
            path: '/users',
            superAdminOnly: true
          },
          {
            text: t('navigation.teams'),
            icon: <GroupIcon />,
            path: '/teams',
            superAdminOnly: true
          },
          {
            text: t('common.models'),
            icon: <SmartToyIcon />,
            path: '/models',
            superAdminOnly: true
          }
        ] : [])
      ]
    }
  ];

  // Flatten menu items for use in page title lookup
  const menuItems: MenuItem[] = menuGroups.flatMap(group => group.items);

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBarStyled position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: { xs: 1.5, sm: 2 },
              marginLeft: { xs: -0.75, sm: 0 },
              padding: { xs: 1, sm: 1.25 },
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Logo removed from header - only visible in drawer */}

          {/* Breadcrumbs */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {/* Use breadcrumbs from context if available, otherwise fallback to page title */}
            {breadcrumbs && breadcrumbs.length > 0 ? (
              <>
                {/* Mobile view: Back button + Current page */}
                <Box
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                    alignItems: 'center',
                    width: '100%',
                    ml: { xs: 0, sm: 0 }
                  }}
                >
                  {breadcrumbs.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Navigate to parent page if available
                        const parentItem = breadcrumbs[breadcrumbs.length - 2];
                        if (parentItem && parentItem.href) {
                          navigate(parentItem.href);
                        }
                      }}
                      sx={{
                        mr: 1,
                        p: 0.5,
                        ml: 0 // Aligned with content
                      }}
                    >
                      <NavigateNextIcon
                        fontSize="small"
                        sx={{
                          transform: 'rotate(180deg)',
                          fontSize: '1.2rem'
                        }}
                      />
                    </IconButton>
                  )}
                  <Typography
                    variant="subtitle1"
                    noWrap
                    component="div"
                    title={breadcrumbs[breadcrumbs.length - 1].label}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 'calc(100% - 40px)', // Account for the back button
                      ml: breadcrumbs.length > 1 ? 0 : 0 // No additional margin needed
                    }}
                  >
                    {breadcrumbs[breadcrumbs.length - 1].label}
                  </Typography>
                </Box>

                {/* Desktop view: Full breadcrumbs */}
                <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    '& .MuiBreadcrumbs-ol': {
                      flexWrap: 'nowrap',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    },
                    '& .MuiBreadcrumbs-li': {
                      display: 'inline-flex',
                      maxWidth: { xs: '200px', sm: '300px', md: '400px' },
                      overflow: 'hidden',
                    },
                    '& .MuiBreadcrumbs-separator': {
                      mx: 0.5,
                    },
                  }}
                  maxItems={5}
                  itemsAfterCollapse={1}
                  itemsBeforeCollapse={1}
                  expandText="..."
                >
                  {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1;

                    return isLast || !item.href ? (
                      <Typography
                        key={item.label}
                        color={isLast ? 'text.primary' : 'text.secondary'}
                        variant="subtitle1"
                        noWrap
                        title={item.label} // Add tooltip on hover
                        sx={{
                          fontWeight: isLast ? 600 : 400,
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}
                      >
                        {item.label}
                      </Typography>
                    ) : (
                      <Link
                        key={item.label}
                        component={RouterLink}
                        to={item.href}
                        color="text.secondary"
                        variant="subtitle1"
                        underline="hover"
                        title={item.label} // Add tooltip on hover
                        sx={{
                          fontSize: '1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                          display: 'block',
                        }}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </>
            ) : (
              <Typography
                variant="subtitle1"
                noWrap
                component="div"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  letterSpacing: '0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  ml: { xs: 0, sm: 0 } // No additional margin needed
                }}
              >
                {menuItems.find(item => item.path === location.pathname)?.text || t('common.dashboard')}
              </Typography>
            )}
          </Box>

          {/* Only show these controls for authenticated users */}
          {!isGuestMode && (
            <>
              {/* Account button */}
              <ClickAwayListener onClickAway={handleUserMenuClose} mouseEvent="click">
                <Box>
                  <Tooltip title={t('common.account')}>
                    <IconButton
                      onClick={handleUserMenuOpen}
                      color="inherit"
                      sx={{
                        ml: { xs: 0.5, sm: 1 },
                        padding: { xs: 0.75, sm: 1 }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: { xs: 26, sm: 28 },
                          height: { xs: 26, sm: 28 },
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          fontSize: '0.875rem',
                        }}
                      >
                        {user?.name?.charAt(0) || user?.email?.charAt(0) || t('common.user', 'U')}
                      </Avatar>
                    </IconButton>
                  </Tooltip>

                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    MenuListProps={{
                      'aria-labelledby': 'user-menu-button',
                      dense: true,
                    }}
                    {...getMenuProps(handleUserMenuClose)}
                  >
                {/* User info section */}
                {user && (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {user.name || user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 0.5 }} />

                {/* Refer a Friend option */}
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserMenuClose();
                    setIsReferralModalOpen(true);
                    onOpenReferralModal?.();
                  }}
                >
                  <ListItemIcon>
                    <PersonAddIcon fontSize="small" />
                  </ListItemIcon>
                  {t('navigation.referFriend')}
                </MenuItem>

                {/* Settings option */}
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUserMenuClose();
                    setIsSettingsModalOpen(true);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('settings.title')}
                    primaryTypographyProps={{ fontWeight: 400 }}
                  />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Terms & Privacy */}
                <MenuItem component={RouterLink} to="/policy" onClick={handleUserMenuClose}>
                  <ListItemIcon>
                    <ArticleIcon fontSize="small" />
                  </ListItemIcon>
                  {t('common.termsAndPrivacy', 'Terms & Privacy')}
                </MenuItem>

                {/* Contact Support */}
                <MenuItem
                  component="a"
                  href={`mailto:${APP_CONFIG.supportEmail}?subject=${encodeURIComponent(t('navigation.supportEmailSubject'))}`}
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon>
                    <SupportIcon fontSize="small" />
                  </ListItemIcon>
                  {t('navigation.contactSupport')}
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Logout */}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  {t('common.logout')}
                </MenuItem>
                  </Menu>
                </Box>
              </ClickAwayListener>

              {/* Notification menu removed */}
            </>
          )}

          {/* Show login button for guest users */}
          {isGuestMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
            >
              {t('auth.signIn')}
            </Button>
          )}
        </Toolbar>
      </AppBarStyled>

      <Drawer
        sx={{
          width: { xs: '100%', sm: drawerWidth },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: drawerWidth },
            boxSizing: 'border-box',
            backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
          },
          // Fix for the persistent overlay issue
          '& .MuiModal-root': {
            position: open ? 'fixed' : 'absolute',
            zIndex: open ? 1200 : -1, // Use fixed value 1200 instead of theme.zIndex.drawer
          },
          '& .MuiBackdrop-root': {
            display: open ? 'block' : 'none',
          },
          // Fix for the MuiDrawer-docked issue
          '& .MuiDrawer-docked': {
            position: open ? 'relative' : 'absolute',
            // When closed, make sure it doesn't affect layout
            '& .MuiPaper-root': {
              position: open ? 'relative' : 'absolute',
            },
          },
          // Hide the drawer completely when closed
          ...(isMobile === false && open === false && {
            display: 'none',
          }),
        }}
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={open}
        {...getDrawerProps(handleDrawerClose)}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <img src={logoSrc} alt={t('common.peerAiLogo', 'PeerAI Logo')} style={{ height: 24, marginRight: 6 }} />
          </Box>
        </DrawerHeader>
        <Divider />
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {menuGroups.map((group, groupIndex) => {
            // Filter items based on permissions
            const visibleItems = group.items.filter(item => {
              // Skip items that require permissions the user doesn't have
              if (item.requiredPermissions && !hasRequiredPermissions(item.requiredPermissions)) {
                return false;
              }
              // Skip items that aren't guest accessible in guest mode
              if (isGuestMode && !item.guestAccessible) {
                return false;
              }
              // Skip admin-only items if user is not an admin
              if (item.adminOnly && !isAdmin) {
                return false;
              }
              // Skip super-admin-only items if user is not a super admin
              if (item.superAdminOnly && !isSuperAdmin) {
                return false;
              }
              return true;
            });

            // Skip empty groups
            if (visibleItems.length === 0) {
              return null;
            }

            return (
              <React.Fragment key={`group-${groupIndex}`}>
                {/* Add divider between groups (but not before the first group) */}
                {groupIndex > 0 && <Divider sx={{ my: 0.75 }} />}

                {/* Group title */}
                {group.title && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      px: 2,
                      py: 0.5,
                      mt: 0.5,
                      mb: 0.25,
                      display: 'block',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {group.title}
                  </Typography>
                )}

                {/* Group items */}
                <List sx={{ py: 0, mb: 0.5 }}>
                  {visibleItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === item.path}
                        sx={{
                          py: 0.5,
                          px: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                          }
                        }}
                        onClick={() => {
                          // Only navigate if we're not already on this path
                          if (location.pathname !== item.path) {
                            // Update the ref before navigation to prevent the useEffect from closing the drawer
                            prevPathnameRef.current = item.path;

                            // Navigate to the new path
                            navigate(item.path);

                            // Close the drawer on mobile after a short delay
                            if (isMobile) {
                              setTimeout(() => {
                                handleDrawerClose();
                              }, 150);
                            }
                          } else if (isMobile) {
                            // If we're already on this path, just close the drawer
                            handleDrawerClose();
                          }
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 32,
                          color: location.pathname === item.path ? 'primary.main' : 'inherit',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.1rem'
                          }
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: location.pathname === item.path ? 500 : 400
                          }}
                        />
                        {(item.superAdminOnly || item.adminOnly) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                            {item.superAdminOnly && (
                              <Tooltip title={t('common.superAdminOnly', 'Super Admin only')} arrow placement="right">
                                <SecurityIcon
                                  sx={{
                                    fontSize: '0.7rem',
                                    opacity: 0.5,
                                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'
                                  }}
                                />
                              </Tooltip>
                            )}
                            {item.adminOnly && (
                              <Tooltip title={t('common.adminOnly', 'Admin only')} arrow placement="right">
                                <LockPersonIcon
                                  sx={{
                                    fontSize: '0.7rem',
                                    opacity: 0.5,
                                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </React.Fragment>
            );
          })}
          </Box>

          {/* Footer in left navigation */}
          <Box
            sx={{
              p: 2,
              mt: 'auto',
              borderTop: 1,
              borderColor: 'divider',
              textAlign: 'center',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(0, 0, 0, 0.02)'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
              {t('common.copyright').replace('{year}', new Date().getFullYear().toString())}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
              <Link
                component={RouterLink}
                to="/policy"
                color="text.secondary"
                sx={{
                  fontSize: '0.7rem',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {t('common.termsOfUse', 'Terms of Use')}
              </Link>
              <Link
                component={RouterLink}
                to="/policy?tab=1"
                color="text.secondary"
                sx={{
                  fontSize: '0.7rem',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {t('common.privacyPolicy', 'Privacy Policy')}
              </Link>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Main open={open} className="always-show-scrollbar">
        <DrawerHeader />
        {/* Content container with centered layout on larger screens */}
        <Box sx={{
          width: '100%',
          padding: { xs: 1.25, sm: 2 },
          paddingTop: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', // Center horizontally
          [theme.breakpoints.up('lg')]: {
            width: '98%',
            maxWidth: '1800px',
            margin: '0 auto',
          },
          [theme.breakpoints.up('xl')]: {
            width: '95%',
            maxWidth: '2000px',
          },
        }}>
          {/* Show announcement banner if enabled or if announcementProps is provided */}
          <Box sx={{ width: '100%', mb: 0.5 }}>
            {(announcementProps?.enabled !== false && announcementProps?.message) ? (
              <AnnouncementBanner
                message={announcementProps.message}
                ctaText={announcementProps.ctaText}
                ctaLink={announcementProps.ctaLink}
                bannerColor={announcementProps.bannerColor}
                textColor={announcementProps.textColor}
                bannerId={announcementProps.bannerId}
              />
            ) : (
              // Default announcement banner
              <AnnouncementBanner
                message={<>
                  {t('common.welcomeBeta', 'Welcome to our Beta, the platform is still under development!')} {t('common.pleaseContact', 'Please')} <Link
                    href="mailto:info@peerdigital.se?subject=Peer%20AI%20Beta%20Feedback"
                    target="_blank"
                    rel="noopener"
                    sx={{
                      color: 'inherit',
                      textDecoration: 'underline',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    {t('common.contactUs', 'contact us')}
                  </Link> {t('common.feedbackMessage', 'to provide feedback or ask questions.')}
                </>}
                bannerId="welcome-beta-2023-en"
                // Using theme colors instead of hardcoded values
              />
            )}
          </Box>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px - 48px)' }}>
            {children}
          </Box>
        </Box>
      </Main>

      {/* Referral Modal */}
      <ReferralModal
        open={isReferralModalOpen}
        {...getDialogProps(() => {
          setIsReferralModalOpen(false);
          onOpenReferralModal?.();
        })}
      />

      {/* Settings Modal */}
      <SettingsModal
        open={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        // Add a key to force re-render when modal is opened
        key={`settings-modal-${isSettingsModalOpen ? 'open' : 'closed'}`}
      />
    </Box>
  );
};

export default DashboardLayout;